import { useMutation, useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { get, post } from "@/api";
import { Text } from "@/atoms";
import { Button } from "@/atoms/button/Button";
import RepeatCash from "@/atoms/icon/icons/RepeatCash.svg";
import {
  AnimatedBackground,
  AnimatedBackgroundConfigType,
} from "@/components/AnimatedBackground/AnimatedBackground";
import { PlatformLogo } from "@/components/PlatformLogo";
import { useCountUp } from "@/hooks";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { useUser } from "@/modules/user/useUser/useUser";
import { queryClient } from "@/pages/Root";
import { useFeatureFlag } from "@/services/Flags";

type ReferralRewardsAcknowledgeRewardsModalProps = {
  readonly platformId: string;
  readonly onCloseAfterInteracting: () => void;
};

export const ReferralRewardsAcknowledgeRewardsModal = ({
  platformId,
  onCloseAfterInteracting,
}: ReferralRewardsAcknowledgeRewardsModalProps) => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const referralRewardsEnabled = useFeatureFlag("referral-rewards-phase-2");

  const { user } = useUser();

  const { data: platform } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
  });

  const { data: unacknowledgedRewardsResponse, isLoading } = useQuery({
    queryKey: [`/incentives/${platformId}/person/${user?.id}/unacknowledged`],
    queryFn: async () => {
      if (!user?.id) return null;
      return await get(
        `/incentives/{platformId}/person/{personId}/unacknowledged`,
        {
          platformId,
          personId: user.id,
        }
      );
    },
    onError: () => null,
    enabled: !!platformId && !!user?.id,
  });

  const regularRewards =
    unacknowledgedRewardsResponse?.awardedReferralRewards?.map((reward) => ({
      ...reward,
      type: "regular" as const,
    })) || [];

  const bonusRewards =
    unacknowledgedRewardsResponse?.bonusAwardedReferralRewards?.map(
      (reward) => ({
        ...reward,
        type: "bonus" as const,
      })
    ) || [];

  const unacknowledgedRewards = [...regularRewards, ...bonusRewards];

  const currentReward = unacknowledgedRewards[0];

  const showModal = unacknowledgedRewards.length > 0 && !isLoading;

  const getRepeatCashValue = () => {
    if (!currentReward) return 0;

    if (currentReward.type === "bonus") {
      const bonusValue =
        currentReward.referralRewardAtTheTime.bonusRepeatCashAmountInCents || 0;
      return bonusValue / 100;
    }

    const value =
      currentReward.referralRewardAtTheTime.repeatCashAmountInCents || 0;
    return value / 100;
  };

  const value = useCountUp({
    to: getRepeatCashValue(),
    from: 0,
    duration: 1500,
    delay: 250,
  });

  useEffect(() => {
    if (showModal) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [showModal]);

  const acknowledgeMutation = useMutation({
    mutationFn: async (rewardId: string) => {
      await post(
        `/incentives/{platformId}/referral/{referralRewardId}/acknowledge/{personId}/{awardedReferralRewardId}`,
        {
          platformId,
          referralRewardId: rewardId,
          personId: user?.id ?? "",
          awardedReferralRewardId: currentReward.awardedReferralRewardId.value,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries([
        `/incentives/${platformId}/person/${user?.id}/unacknowledged`,
      ]);

      queryClient.refetchQueries([
        `/incentives/${platformId}/person/${user?.id}/unacknowledged`,
      ]);

      queryClient.invalidateQueries([`/user`]);

      queryClient.refetchQueries([`/user`]);

      console.log("user after acknowledge", user);

      setHasInteracted(true);
    },
    onError: (error: Error) => {
      console.log("error", error);
    },
  });

  useEffect(() => {
    if (hasInteracted && !showModal) {
      onCloseAfterInteracting();
    }
  }, [hasInteracted, showModal]);

  const handleAcknowledge = () => {
    if (currentReward) {
      acknowledgeMutation.mutate(currentReward.referralRewardAtTheTime.id);
    }
  };

  const getRewardTitle = () => {
    if (!currentReward) return "";

    if (currentReward.type === "bonus") {
      return "Bonus Reward!";
    }

    if (
      currentReward.type === "regular" &&
      "referredPersonName" in currentReward &&
      currentReward.referredPersonName
    ) {
      return `Thanks for referring ${getPersonFirstNameLastInitial(
        currentReward.referredPersonName
      )}!`;
    }

    return "New Reward!";
  };

  const getRewardDescription = () => {
    if (!currentReward) return "";

    if (currentReward.type === "bonus") {
      return currentReward.referralRewardAtTheTime.bonusRewardTitle.text;
    }

    return currentReward.referralRewardAtTheTime.rewardTitle.text;
  };

  const renderHeader = () => (
    <div className="flex items-center justify-end px-4 py-2">
      <button
        onClick={handleAcknowledge}
        className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
        aria-label="Close modal"
      >
        <X size={24} />
      </button>
    </div>
  );

  const renderBonusContent = () => {
    if (!currentReward || currentReward.type !== "bonus") return null;

    const referredNames =
      "referredPersonNames" in currentReward
        ? currentReward.referredPersonNames
        : [];

    return (
      <div className="flex flex-1 flex-col items-center justify-between">
        <motion.div
          key={currentReward.awardedAtUtc.toString()}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="flex w-full flex-col items-center gap-4 text-center"
        >
          <PlatformLogo
            logoUrl={platform?.logoImageUrl}
            platformName={platform?.name}
            className="h-12 w-auto max-w-[180px]"
          />

          <Text
            text="We want to send an extra special thank you."
            variant="h1"
            className="whitespace-pre-line font-serif text-2xl font-medium italic leading-snug text-gray-800"
          />

          <div className="flex flex-col items-center gap-3">
            <Text
              text="Thanks for referring:"
              variant="body1"
              className="text-gray-600"
            />
            <div className="flex flex-wrap justify-center gap-2">
              {referredNames?.map((name, index) => (
                <div
                  key={index}
                  className="rounded-full bg-white/80 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm"
                >
                  {getPersonFirstNameLastInitial(name)}
                </div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="relative mt-4 w-full"
            style={{ aspectRatio: "1.586" }}
          >
            <div className="relative h-full w-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#D4876F] via-[#ECA696] to-[#C98B6B] px-8 py-16 shadow-2xl">
              {/* Animated background - creates flowing metallic shine */}
              <AnimatedBackground
                durationSeconds={10}
                className="absolute inset-0 h-full w-full"
                backgroundConfig={{
                  baseColor: "#D4876F",
                  color1: "#ECA696",
                  color2: "#ffffff",
                  tint1Percent: 40,
                  tint2Percent: 50,
                  spot1SizePercent: 70,
                  spot2SizePercent: 65,
                  vignetteOpacity: 0.2,
                }}
              />

              {/* Metallic shine effect - Rose gold style */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-amber-900/20" />
              <div className="absolute inset-0 bg-gradient-to-tl from-white/30 via-transparent to-transparent" />

              {/* Subtle card pattern - mirrors RepeatCash card */}
              <div
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                  backgroundSize: "32px 32px",
                }}
              />

              {/* Plus signs - more decorative */}
              <div className="absolute right-8 top-6 text-4xl font-light text-amber-950/30">
                +
              </div>
              <div className="absolute bottom-6 left-8 text-5xl font-light text-amber-950/40">
                +
              </div>
              <div className="absolute left-1/4 top-10 text-3xl font-light text-amber-950/25">
                +
              </div>
              <div className="absolute bottom-10 right-1/4 text-3xl font-light text-amber-950/25">
                +
              </div>
              <div className="absolute right-16 top-1/2 text-2xl font-light text-amber-950/20">
                +
              </div>
              <div className="absolute bottom-1/3 left-16 text-2xl font-light text-amber-950/20">
                +
              </div>

              {/* Content */}
              <div className="relative flex h-full items-center justify-center">
                <div className="flex items-center gap-3 font-sans text-3xl font-bold drop-shadow-sm">
                  {currentReward.referralRewardAtTheTime.referralRewardType ===
                  "RepeatCash" ? (
                    <>
                      <RepeatCash className="h-20 w-20 text-black" />
                      <Text
                        text={`$${value}`}
                        variant="v3-h1"
                        className="text-6xl font-bold not-italic text-black"
                      />
                    </>
                  ) : (
                    <span className="font-bold not-italic text-black">
                      {
                        currentReward.referralRewardAtTheTime.bonusRewardTitle
                          ?.text
                      }
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  };

  const renderRegularContent = () => {
    if (!currentReward || currentReward.type !== "regular") return null;

    const rewardType = currentReward.referralRewardAtTheTime.referralRewardType;

    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <motion.div
          key={currentReward.awardedAtUtc.toString()}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="flex w-full flex-col items-center gap-4 text-center"
        >
          <PlatformLogo
            logoUrl={platform?.logoImageUrl}
            platformName={platform?.name}
            className="h-16 w-auto max-w-[200px]"
          />

          <Text text={getRewardTitle()} variant="v3-h2" />

          <div className="flex flex-col items-center gap-3">
            <Text
              text={`You received a reward for your referral.`}
              variant="body1"
              className="text-gray-600"
            />
          </div>

          <motion.div
            className="flex items-center gap-4 rounded-full bg-white/20 px-6 py-4 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            {rewardType === "RepeatCash" && (
              <>
                <RepeatCash className="h-16 w-16" />
                <Text text={`$${value}`} variant="v3-h2" />
              </>
            )}
            {rewardType !== "RepeatCash" && (
              <Text text={getRewardDescription()} variant="v3-h3" />
            )}
          </motion.div>
        </motion.div>
      </div>
    );
  };

  const renderFooter = () => {
    const buttonText =
      unacknowledgedRewards.length > 1 ? "Next Reward" : "Bank it";

    return (
      <Button
        fullWidth
        size="large"
        onClick={handleAcknowledge}
        disabled={acknowledgeMutation.isLoading}
      >
        {acknowledgeMutation.isLoading ? "Acknowledging..." : buttonText}
      </Button>
    );
  };

  const renderContent = () => {
    if (!currentReward)
      return (
        <div className="flex min-h-[50vh] flex-1 items-center justify-center text-center text-2xl font-bold">
          No rewards to acknowledge
        </div>
      );

    return currentReward.type === "bonus"
      ? renderBonusContent()
      : renderRegularContent();
  };

  if (!referralRewardsEnabled) return null;

  return (
    <AnimatePresence>
      {showModal && (
        <motion.div
          className="fixed inset-0 z-50 mx-auto flex h-full min-h-screen w-full flex-col items-center justify-center sm:max-w-[390px]"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <div className="relative flex h-full w-full flex-col overflow-y-auto bg-white">
            <MobileViewportContainer
              header={renderHeader()}
              bottomCta={renderFooter()}
              contentClassName="px-8 pb-4 pt-0"
              showAnimatedBackground={true}
            >
              {renderContent()}
            </MobileViewportContainer>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const goldConfig: AnimatedBackgroundConfigType = {
  baseColor: "#f5f0e8", // Warm beige/cream background
  color1: "#d4af37", // Metallic gold
  color2: "#c9a030", // Darker gold
  tint1Percent: 20,
  tint2Percent: 18,
  spot1SizePercent: 55,
  spot2SizePercent: 50,
  vignetteOpacity: 0.15,
};

function getPersonFirstNameLastInitial(name: string) {
  const nameParts = name.split(" ");

  if (nameParts.length < 2) {
    return name;
  }

  const firstName =
    nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1).toLowerCase();
  const lastInitial = nameParts[1].charAt(0).toUpperCase();
  return `${firstName} ${lastInitial}.`;
}
